import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Table, Typography } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

import {
    IPayphonePayment,
    IPayphoneRusherSummary,
    IPayphoneStoreSummary,
    OrdersAdminStore,
} from '../../../../stores/Admin/OrderStore';
import { CompanyStore } from '../../../../stores/Admin/CompanyAdminStores';
import { DriverAdminStore } from '../../../../stores/Admin/DriverAdminStore';
import { Helmet } from 'react-helmet';
import { ColumnsType } from 'antd/es/table';
import DateRangePicker from '../../../../components/DateRangePicker';
import { StoresPicker, DriversPicker } from '../../../../components/dropdowns';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

const { Title } = Typography;

const money = (value?: number | string | null) => {
    const number = Number(value || 0);
    return number.toFixed(2);
};

const ReportPaymentsPayphone = () => {
    const history = useHistory();
    const location = useLocation();

    const getDateFromQuery = () => {
        const query = new URLSearchParams(location.search);
        const min = query.get('created_time_min');
        const max = query.get('created_time_max');
        if (min && max) {
            return [moment(min), moment(max)];
        }
        return undefined;
    };

    const getSelectFromQuery = (key: string) => {
        const query = new URLSearchParams(location.search);
        const value = query.get(key);
        return value ? { value, label: value } : undefined;
    };

    const [date, setDate] = useState<any>(getDateFromQuery);
    const [store, setStore] = useState<{ value: string; label: string } | undefined>(() => getSelectFromQuery('owner'));
    const [rusher, setRusher] = useState<{ value: string; label: string } | undefined>(() =>
        getSelectFromQuery('delivery_driver')
    );

    useEffect(() => {
        CompanyStore.getCompanies({});
        DriverAdminStore.getDrivers({});
    }, []);

    const updateQuery = (updates: Record<string, string | undefined>) => {
        const query = new URLSearchParams(location.search);
        Object.entries(updates).forEach(([key, value]) => {
            if (value) {
                query.set(key, value);
            } else {
                query.delete(key);
            }
        });
        history.replace({ search: query.toString() });
    };

    const handleDateChange = (value: any) => {
        setDate(value);
        if (value) {
            updateQuery({
                created_time_min: value[0].format('YYYY-MM-DD'),
                created_time_max: value[1].format('YYYY-MM-DD'),
            });
        }
    };

    const handleStoreChange = (value: any) => {
        setStore(value);
        updateQuery({ owner: value?.value });
    };

    const handleRusherChange = (value: any) => {
        setRusher(value);
        updateQuery({ delivery_driver: value?.value });
    };

    useEffect(() => {
        if (!date) {
            return;
        }
        const params: any = {
            created_time_min: date[0].format('YYYY-MM-DD'),
            created_time_max: date[1].format('YYYY-MM-DD'),
        };

        if (store?.value) {
            params['owner'] = store.value;
        }

        if (rusher?.value) {
            params['delivery_driver'] = rusher.value;
        }

        OrdersAdminStore.getReportPayphonePayments(params);
        OrdersAdminStore.getReportPayphonePaymentsSummary(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, store, rusher]);

    const columns: ColumnsType<IPayphonePayment> = [
        {
            title: 'Orden',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Fecha',
            dataIndex: 'created_time',
            key: 'created_time',
            render: value => (
                <div>
                    <div>{dayjs(value).format('DD/MM/YYYY hh:mm A')}</div>
                    <div>{dayjs(value).fromNow()}</div>
                </div>
            ),
        },
        {
            title: 'Restaurante',
            dataIndex: 'store',
            key: 'store',
        },
        {
            title: 'Pago Restaurante',
            dataIndex: 'restaurant_payment',
            key: 'restaurant_payment',
            render: value => money(value),
        },
        {
            title: 'Pago Rush',
            dataIndex: 'rush_payment',
            key: 'rush_payment',
            render: value => (value === null || value === undefined ? 'Pendiente' : money(value)),
        },
        {
            title: 'Tip',
            dataIndex: 'tip',
            key: 'tip',
            render: value => money(value),
        },
        {
            title: 'Pago PayPhone',
            dataIndex: 'payphone_payment',
            key: 'payphone_payment',
            render: value => money(value),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: value => money(value),
        },
    ];

    const storeColumns: ColumnsType<IPayphoneStoreSummary> = [
        { title: 'Tienda', dataIndex: 'store', key: 'store' },
        {
            title: 'Pago a Tienda',
            dataIndex: 'net',
            key: 'net',
            render: value => money(value),
        },
    ];

    const rusherColumns: ColumnsType<IPayphoneRusherSummary> = [
        { title: 'Rusher', dataIndex: 'rusher', key: 'rusher' },
        {
            title: 'Carreras',
            dataIndex: 'delivery',
            key: 'delivery',
            render: value => money(value),
        },
        { title: 'Tip', dataIndex: 'tip', key: 'tip', render: value => money(value) },
        {
            title: 'Pago a Rusher',
            dataIndex: 'total',
            key: 'total',
            render: value => money(value),
        },
    ];

    const data = OrdersAdminStore.payphonePayments;
    const storesSummary = OrdersAdminStore.payphoneStoresSummary;
    const rushersSummary = OrdersAdminStore.payphoneRushersSummary;

    const sum = (key: keyof IPayphonePayment) => data.reduce((total, order) => total + Number(order[key] || 0), 0);
    const sumStores = (key: keyof IPayphoneStoreSummary) =>
        storesSummary.reduce((total, s) => total + Number(s[key] || 0), 0);
    const sumRushers = (key: keyof IPayphoneRusherSummary) =>
        rushersSummary.reduce((total, r) => total + Number(r[key] || 0), 0);

    return (
        <div className={'flex flex-col p-4'}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Reporte de Pagos PayPhone</title>
            </Helmet>
            <div className={'w-full pt-4 flex flex-row items-center justify-center flex-wrap gap-2'}>
                <div className={'ml-2'}>
                    <label>Fecha:</label> <DateRangePicker value={date} onChange={handleDateChange} />
                </div>
                <div className={'ml-2'} style={{ minWidth: 200 }}>
                    <label>Tienda:</label> <StoresPicker value={store} onChange={handleStoreChange} />
                </div>
                <div className={'ml-2'} style={{ minWidth: 200 }}>
                    <label>Rusher:</label> <DriversPicker value={rusher} onChange={handleRusherChange} />
                </div>
            </div>

            <div className={'pt-4'}>
                <Table<IPayphonePayment>
                    rowKey="id"
                    pagination={false}
                    columns={columns}
                    loading={OrdersAdminStore.loading}
                    dataSource={data}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={3}>
                                    Totales
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>{money(sum('restaurant_payment'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>{money(sum('rush_payment'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>{money(sum('tip'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={4}>{money(sum('payphone_payment'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>{money(sum('total'))}</Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </div>

            <div className={'pt-8'}>
                <Title level={4}>Resumen por Tienda (cuánto pagar)</Title>
                <Table<IPayphoneStoreSummary>
                    rowKey="store"
                    pagination={false}
                    columns={storeColumns}
                    loading={OrdersAdminStore.loading}
                    dataSource={storesSummary}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>Totales</Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>{money(sumStores('net'))}</Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </div>

            <div className={'pt-8'}>
                <Title level={4}>Resumen por Rusher (cuánto pagar)</Title>
                <Table<IPayphoneRusherSummary>
                    rowKey="rusher"
                    pagination={false}
                    columns={rusherColumns}
                    loading={OrdersAdminStore.loading}
                    dataSource={rushersSummary}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>Totales</Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>{money(sumRushers('delivery'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>{money(sumRushers('tip'))}</Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>{money(sumRushers('total'))}</Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </div>
        </div>
    );
};

export default observer(ReportPaymentsPayphone);
